<template>
  <div class="widget-google-map">
    <div v-if="!location?.latitude" class="widget-google-map_empty">
      <WidgetIcon :icon="mainIcon" class="widget-primary-icon" />
      <div class="widget-google-map_empty--button-wrapper">
        <WButton
          class="widget-google-map_empty--button"
          @click="isMapOpen = true"
          :type="checkTheme('common') ? 'primary' : 'outline'"
        >
          <div class="flex w-full gap-3 !transition-none">
            <WidgetIcon v-if="btnIcon" :icon="btnIcon" height="1.5rem" class="!transition-none" />
            <span class="widget-google-map_empty--button-text !transition-none">{{ title }}</span>
          </div>
        </WButton>
      </div>
    </div>
    <div v-else>
      <div ref="previewWidgetMap" class="widget-google-map_preview"></div>
      <div v-if="location" class="body1">
        <p class="widget-google-map_preview_label">{{ locationLabel || 'Location' }}:</p>
        <p>
          {{ location.formattedAddress }}
        </p>
        <template v-if="!!location?.w3w">
          <p class="widget-google-map_preview_label">{{ w3wLabel || 'What3words Location' }}:</p>
          <a :href="`https://what3words.com/${location.w3w}`" target="_blank">
            <span class="text-[#e11f26]">/// </span>{{ location.w3w }}
          </a>
        </template>
      </div>
    </div>
    <Modal
      v-if="isMapOpen"
      v-model="isMapOpen"
      size="full"
      body-class="!max-h-dvh !h-dvh !my-0 justify-center items-center flex !p-0 w-full"
    >
      <template v-if="!isMapLoading">
        <div v-if="!isMobile" class="search flex items-center">
          <AddressSearch
            v-if="!isMapLoading"
            :text="location?.formattedAddress"
            icon="mdi-magnify"
            @place-changed="locationChange"
            @clear="location = {} as ILocationInfo"
          />
          <WButton type="secondary" @click="cancelLocationSelect">
            {{ cancelText }}
          </WButton>
          <WButton type="primary" :disabled="!location" @click="confirm">
            {{ confirmText }}
          </WButton>
        </div>
        <div v-else class="search flex flex-col items-center gap-4">
          <div class="flex w-full items-center justify-between rounded-t-custom">
            <h3 class="widget-google-map_search--title">{{ title }}</h3>
            <WButton type="ghost" @click="cancelLocationSelect">
              <slot name="close-icon">
                <WidgetIcon :icon="cancelIcon" height="24" class="widget-secondary-icon" />
              </slot>
            </WButton>
          </div>
          <div class="flex w-full items-center justify-between gap-4">
            <AddressSearch
              v-if="!isMapLoading"
              :text="location?.formattedAddress"
              icon="mdi-magnify"
              @place-changed="locationChange"
              @clear="location = {} as ILocationInfo"
            />
            <WButton type="primary" @click="confirm" :disabled="!location"> {{ confirmText }} </WButton>
          </div>
        </div>
      </template>
      <div v-else class="flex items-center justify-center">
        <img :src="MapSkeleton" class="map-skeleton-bg absolute top-0 z-0 h-full w-full animate-pulse object-cover" />
        <div class="absolute top-0 flex w-full items-center justify-end p-4 pt-6">
          <WButton type="ghost" @click="cancelLocationSelect">
            <slot name="close-icon">
              <WidgetIcon :icon="cancelIcon" height="24" class="widget-secondary-icon" />
            </slot>
          </WButton>
        </div>
        <WidgetIcon :icon="themeIcons.marker" class="widget-primary-icon z-10 size-12 animate-pulse" />
      </div>
      <div v-show="!isMapLoading" class="h-full w-full">
        <div ref="axitechWidgetMap" id="widget-google-map_map"></div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
  import { computed, defineAsyncComponent, ref, toRaw, watch } from 'vue';
  import { initializeApis, useGoogleMaps } from 'src/composables/googleMaps';
  import { ILocationInfo } from 'src/utils/map';
  import WButton from './Button.vue';
  import AddressSearch from './AddressSearch.vue';
  import Modal from './Modal.vue';
  import { useBreakpoints } from 'src/utils/helpers';
  import { checkTheme, themeIcons } from 'src/utils/config';
  import MapSkeleton from 'src/assets/images/map_skeleton.png';

  const { isMobile } = useBreakpoints();
  const props = withDefaults(
    defineProps<{
      modelValue?: ILocationInfo;
      title?: string;
      isOpen?: boolean;
      confirmText?: string;
      w3wLabel?: string;
      locationLabel?: string;
      cancelText?: string;
      cancelIcon?: string;
      mainIcon?: string;
      searchIcon?: string;
      markerIcon?: string;
      btnIcon?: string;
    }>(),
    {
      modelValue: undefined,
      title: 'Submit collision location',
      confirmText: 'confirm',
      cancelText: 'cancel',
      w3wLabel: 'What3words Location',
      locationLabel: 'Location',
      cancelIcon: themeIcons.value.close,
      mainIcon: undefined,
      searchIcon: themeIcons.value.search,
      markerIcon: '',
      btnIcon: '',
    }
  );

  const WidgetIcon = defineAsyncComponent(() => import('./WidgetIcon.vue'));

  const emit = defineEmits<{
    (e: 'update:modelValue', value: ILocationInfo | undefined): void;
    (e: 'update:isOpen', value: boolean): void;
  }>();

  const axitechWidgetMap = ref<HTMLElement>();
  const previewWidgetMap = ref<HTMLElement>();
  const location = computed({
    get: () => {
      return props.modelValue;
    },
    set: (value) => {
      emit('update:modelValue', value);
    },
  });
  const initialLocation = ref(structuredClone(toRaw(location.value)));
  const isMapOpen = computed({
    get: () => props.isOpen,
    set: (value) => emit('update:isOpen', value),
  });
  const isMapLoading = ref(true);
  const { load, getLocation, placeChanged, loadPromise } = useGoogleMaps(axitechWidgetMap, (place) => {
    location.value = place;
  });
  const { loadPreview, placeChanged: previewPlaceChanged } = useGoogleMaps(previewWidgetMap);

  function locationChange(place: ILocationInfo) {
    location.value = place;
    placeChanged(place, { zoom: 16 });
  }

  function confirm() {
    if (!location.value) return;
    isMapOpen.value = false;
    previewPlaceChanged(location.value, { zoom: 16 });
  }

  function cancelLocationSelect() {
    isMapOpen.value = false;
    location.value = structuredClone(toRaw(initialLocation.value));
    if (initialLocation.value) {
      previewPlaceChanged(initialLocation.value, { zoom: 16 });
    }
  }

  watch(axitechWidgetMap, async () => {
    if (!axitechWidgetMap.value) return;
    await initializeApis();
    const start = new Date().getTime();
    try {
      isMapLoading.value = true;
      await loadPromise;
      const defaultLocation = location.value?.latitude ? location.value : await getLocation();
      location.value = defaultLocation;
      await load({
        center: new google.maps.LatLng(defaultLocation.latitude, defaultLocation.longitude),
      });

      if (defaultLocation?.latitude && defaultLocation?.longitude) {
        placeChanged(defaultLocation, { zoom: 16 });
      }
    } finally {
      const end = new Date().getTime();
      const difference = end - start;
      const timeout = difference < 1000 ? 1000 - difference : 0;
      setTimeout(() => {
        isMapLoading.value = false;
      }, timeout);
    }
  });

  watch(previewWidgetMap, async () => {
    if (!previewWidgetMap.value) return;
    await initializeApis();
    await loadPromise;
    const defaultLocation = location.value?.latitude ? location.value : await getLocation();
    await loadPreview({
      center: new google.maps.LatLng(defaultLocation.latitude, defaultLocation.longitude),
    });
    location.value = defaultLocation;
  });

  watch(isMapOpen, () => {
    if (location.value && isMapOpen.value) {
      initialLocation.value = structuredClone(toRaw(location.value));
    }
  });

  defineExpose({
    locationChange,
  });
</script>
